import { ChevronsUpDown, RotateCcw } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from './button'
import { Input } from './input'

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu'

export type FilterMultiSelectItem = {
  value: string
  label: string
}

export type FilterMultiSelectProps = {
  label: string
  values: readonly string[]
  onChange: (values: string[]) => void
  items: readonly FilterMultiSelectItem[]
  allLabel: string
  onOpenChange?: (open: boolean) => void
  hideLabel?: boolean
  className?: string
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
  }
  loadMore?: {
    onLoad: () => void
    loading: boolean
  }
  error?: string
  emptyOption?: {
    selected: boolean
    label: string
    onChange: (selected: boolean) => void
  }
}

function FilterMultiSelect({
  label,
  values,
  onChange,
  items,
  allLabel,
  onOpenChange,
  hideLabel,
  className,
  search,
  loadMore,
  error,
  emptyOption,
}: FilterMultiSelectProps) {
  const selectedItems = items.filter((item) => values.includes(item.value))
  const triggerLabel = emptyOption?.selected
    ? emptyOption.label
    : selectedItems.length === 0
    ? allLabel
    : selectedItems.map((item) => item.label).join(', ')
  const menu = (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger
        data-slot="filter-multi-select"
        aria-label={`Filter by ${label.toLowerCase()}`}
        className={cn(
          'border-input bg-input/20 dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/30 data-popup-open:border-ring text-foreground flex h-7 w-auto min-w-0 items-center justify-between gap-2 rounded-md border px-2 py-0.5 text-sm transition-colors outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-xs/relaxed',
          className,
        )}
      >
        <span className="min-w-0 max-w-44 truncate" title={triggerLabel}>
          {triggerLabel}
        </span>
        <ChevronsUpDown
          aria-hidden
          className="text-muted-foreground size-3.5 shrink-0"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[min(20rem,var(--available-height))] min-w-[var(--anchor-width)] overflow-y-auto"
      >
        <DropdownMenuItem
          disabled={values.length === 0 && !emptyOption?.selected}
          onClick={() => {
            if (emptyOption?.selected) emptyOption.onChange(false)
            else onChange([])
          }}
        >
          <RotateCcw aria-hidden className="size-3.5" />
          Clear filter
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {search ? (
          <div className="p-1">
            <Input
              aria-label={`Search ${label} options`}
              value={search.value}
              onChange={(event) => search.onChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key.length === 1) event.stopPropagation()
              }}
              placeholder={search.placeholder ?? 'Search options…'}
            />
          </div>
        ) : null}
        {items.map((item) => (
          <DropdownMenuCheckboxItem
            key={item.value}
            checked={values.includes(item.value)}
            onCheckedChange={(checked) => {
              onChange(
                items
                  .filter((candidate) =>
                    candidate.value === item.value
                      ? checked
                      : values.includes(candidate.value))
                  .map((candidate) => candidate.value),
              )
            }}
          >
            {item.label}
          </DropdownMenuCheckboxItem>
        ))}
        {emptyOption ? (
          <DropdownMenuCheckboxItem
            checked={emptyOption.selected}
            onCheckedChange={emptyOption.onChange}
          >
            {emptyOption.label}
          </DropdownMenuCheckboxItem>
        ) : null}
        {loadMore ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="m-1 w-[calc(100%-0.5rem)]"
            disabled={loadMore.loading}
            onClick={loadMore.onLoad}
          >
            {loadMore.loading ? 'Loading…' : 'Load more options'}
          </Button>
        ) : null}
        {error ? <p role="alert" className="text-destructive px-2 py-1 text-xs">{error}</p> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  if (hideLabel) return menu

  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {menu}
    </div>
  )
}

export { FilterMultiSelect }
